import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Check, ChefHat, Loader2, Plus, Search, Trash2, UtensilsCrossed, X } from "lucide-react";
import { IngredientPicker } from "@/components/inventory/IngredientPicker";
import { cachedGet } from "@/lib/refCache";

/**
 * Session 41 — Inventory → Recipes, redesigned for speed.
 *
 *   Search menu item → results appear immediately → tap one → recipe opens
 *   → "+ Add ingredient" → searchable picker → quantity → Save.
 *
 * No select-then-click-another-bar. Costing comes from the existing costing
 * engine (/owner/costing/coverage) — nothing is recomputed here.
 */
const UNITS = ["kg", "g", "L", "ml", "pcs", "tots"];
const fmt = (n) => (Number.isFinite(Number(n)) ? (Number.isInteger(Number(n)) ? String(n) : Number(n).toFixed(2).replace(/\.?0+$/, "")) : "—");
const money = (n) => (n == null || !Number.isFinite(Number(n)) ? "—" : `R${Number(n).toFixed(2)}`);

function unitOptionsFor(ing) {
  const base = ing?.unit || "pcs";
  const bridge = ing && Number(ing.weight_per_unit_g) > 0;
  const bridgeUnit = ing?.piece_unit || "g";
  if (base === "g" || base === "kg") return ["g", "kg"];
  if (base === "ml" || base === "L") return ["ml", "L"];
  if (base === "pcs") return bridge ? (bridgeUnit === "ml" ? ["pcs", "ml", "L"] : ["pcs", "g", "kg"]) : ["pcs"];
  if (base === "tots") return ["tots"];
  return [base];
}

export const RecipesPanel = ({ http, authHeaders, menuItems = [], ingredients = [], onSaved }) => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // menu item
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [costing, setCosting] = useState(null); // id → coverage row
  const [summary, setSummary] = useState(null); // items with recipes
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  // Session 66 — STOCK BELONGS TO A DEPARTMENT. A recipe may only use the
  // ingredients of the menu item's own department (Beach Bar item → Beach
  // Bar stock), so a sale from one bar can never deduct the other bar's
  // stock. Departments are loaded once; menu items carry the department
  // NAME, ingredients carry department_id + department_name.
  const [departments, setDepartments] = useState([]);
  const [deptFilter, setDeptFilter] = useState("all");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await cachedGet(http, "/owner/departments", { headers: authHeaders });
        const list = (r.data?.departments || []).filter((d) => d && d.active !== false && (d.route_to_kitchen || d.route_to_bar));
        if (alive) setDepartments(list);
      } catch (_) { /* optional */ }
    })();
    return () => { alive = false; };
  }, [http, authHeaders]);
  const deptByName = useMemo(() => { const m = new Map(); (departments || []).forEach((d) => m.set(String(d.name || "").trim().toLowerCase(), d)); return m; }, [departments]);
  const deptOfItem = useCallback((mi) => {
    const name = String(mi?.department || "").trim();
    if (!name) return null;
    return deptByName.get(name.toLowerCase()) || { id: null, name, route_to_bar: /bar/i.test(name), route_to_kitchen: !/bar/i.test(name) };
  }, [deptByName]);
  const ingBelongsTo = useCallback((ing, dept) => {
    if (!dept) return true;
    if (ing.department_id && dept.id) return ing.department_id === dept.id;
    const ingDeptName = String(ing.department_name || "").trim().toLowerCase();
    if (ingDeptName) return ingDeptName === String(dept.name || "").trim().toLowerCase();
    // Legacy ingredient without a department → match the station bucket.
    const station = dept.route_to_bar && !dept.route_to_kitchen ? "bar" : "kitchen";
    return (ing.category === "bar" ? "bar" : "kitchen") === station;
  }, []);

  const loadCosting = useCallback(async () => {
    try {
      const r = await cachedGet(http, "/owner/costing/coverage", { headers: authHeaders });
      const map = {};
      (r.data?.items || []).forEach((row) => { map[row.id] = row; });
      setCosting(map);
    } catch (_) { /* optional */ }
  }, [http, authHeaders]);
  const loadSummary = useCallback(async () => {
    try {
      const r = await cachedGet(http, "/owner/recipes", { headers: authHeaders });
      setSummary(r.data?.recipes || []);
    } catch (_) { setSummary([]); }
  }, [http, authHeaders]);
  useEffect(() => { loadCosting(); loadSummary(); }, [loadCosting, loadSummary]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const withRecipe = new Set((summary || []).map((r) => r.id));
    return (menuItems || [])
      .filter((mi) => (mi.name || "").toLowerCase().includes(q))
      .filter((mi) => deptFilter === "all" || String(mi.department || "").trim().toLowerCase() === deptFilter)
      .sort((a, b) => {
        const as = (a.name || "").toLowerCase().startsWith(q) ? 0 : 1;
        const bs = (b.name || "").toLowerCase().startsWith(q) ? 0 : 1;
        return as - bs || (a.name || "").localeCompare(b.name || "");
      })
      .slice(0, 12)
      .map((mi) => ({ ...mi, hasRecipe: withRecipe.has(mi.id) }));
  }, [query, menuItems, summary, deptFilter]);

  const open = async (mi) => {
    setSelected(mi);
    setQuery("");
    setDirty(false);
    setLoading(true);
    try {
      const r = await http.get(`/owner/menu-items/${mi.id}/recipe`, { headers: authHeaders });
      setLines((r.data.lines || []).map((l, i) => ({ _k: `${l.ingredient_id || "line"}-${i}-${Date.now()}`, ingredient_id: l.ingredient_id, quantity: l.quantity, unit: l.unit || l.ingredient_unit || "pcs" })));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load this recipe.");
      setLines([]);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!selected) return;
    const bad = lines.find((l) => !l.ingredient_id || Number(l.quantity) <= 0);
    if (bad) { toast.error("Every row needs an ingredient and a quantity above 0."); return; }
    // Session 66 — strict department isolation (also enforced server-side).
    const dept = deptOfItem(selected);
    const foreign = lines.map((l) => ingById.get(l.ingredient_id)).filter((ing) => ing && !ingBelongsTo(ing, dept));
    if (dept && foreign.length) {
      toast.error(`${foreign.map((i) => i.name).join(", ")} belong${foreign.length === 1 ? "s" : ""} to ${foreign[0].department_name || "another department"} — a ${dept.name} recipe can only use ${dept.name} stock.`);
      return;
    }
    setSaving(true);
    try {
      await http.put(`/owner/menu-items/${selected.id}/recipe`, { lines: lines.map((l) => ({ ingredient_id: l.ingredient_id, quantity: Number(l.quantity), unit: l.unit || undefined })) }, { headers: authHeaders });
      toast.success(`Recipe saved for ${selected.name}.`);
      setDirty(false);
      await Promise.all([loadCosting(), loadSummary()]);
      onSaved?.(selected.id);
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Could not save the recipe.");
    } finally {
      setSaving(false);
    }
  };

  const setLine = (idx, patch) => { setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l))); setDirty(true); };
  const addLine = () => { setLines((ls) => [...ls, { _k: `k${Date.now()}${Math.random().toString(36).slice(2, 7)}`, ingredient_id: "", quantity: 1, unit: "" }]); setDirty(true); };
  const removeLine = (idx) => { setLines((ls) => ls.filter((_, i) => i !== idx)); setDirty(true); };

  const onKey = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); open(results[highlight] || results[0]); }
  };

  const ingById = useMemo(() => { const m = new Map(); (ingredients || []).forEach((i) => m.set(i.id, i)); return m; }, [ingredients]);
  const cost = selected ? costing?.[selected.id] : null;
  const costLine = (iid) => (cost?.lines || []).find((l) => l.ingredient_id === iid);
  // Session 66 — only the item's own department's ingredients are offered.
  const selectedDept = selected ? deptOfItem(selected) : null;
  const pickerIngredients = useMemo(() => (selectedDept ? (ingredients || []).filter((i) => ingBelongsTo(i, selectedDept)) : (ingredients || [])), [ingredients, selectedDept, ingBelongsTo]);
  const deptChips = useMemo(() => {
    const names = new Map();
    (departments || []).forEach((d) => names.set(String(d.name).trim().toLowerCase(), d.name));
    (menuItems || []).forEach((mi) => { const n = String(mi.department || "").trim(); if (n && !names.has(n.toLowerCase())) names.set(n.toLowerCase(), n); });
    return [...names.entries()].map(([key, label]) => ({ key, label }));
  }, [departments, menuItems]);
  const itemDeptKey = (mi) => String(mi?.department || "").trim().toLowerCase();

  // ── Recipe editor ─────────────────────────────────────────────────────
  const editor = selected ? (
    <Card className="rounded-[24px] border-border bg-card" data-testid="recipe-editor">
      <CardContent className="space-y-4 p-4 sm:p-5">
        <div className="flex flex-wrap items-start gap-3">
          <Button variant="ghost" size="icon" className="h-11 w-11 rounded-xl" onClick={() => { setSelected(null); setTimeout(() => inputRef.current?.focus(), 50); }} aria-label="Back to search" data-testid="recipe-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Recipe · per 1 portion</p>
            <h3 className="truncate font-['Space_Grotesk'] text-2xl font-semibold" data-testid="recipe-item-title">{selected.name}</h3>
            <p className="text-xs text-muted-foreground" data-testid="recipe-cost-summary">
              Sells at <b className="text-foreground">{money(selected.price)}</b>
              {cost && cost.cost != null ? <> · Costs <b className="text-foreground">{money(cost.cost)}</b> · Margin <b className="text-foreground">{cost.margin_pct != null ? `${cost.margin_pct}%` : "—"}</b></> : cost && cost.status === "partial" ? " · Some ingredients have no cost yet" : null}
            </p>
            {selectedDept ? (
              <p className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-2 py-0.5 text-[11px] font-medium" data-testid="recipe-department-badge">
                <ChefHat className="h-3 w-3" /> {selectedDept.name} recipe · deducts {selectedDept.name} stock only
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300" data-testid="recipe-department-missing">
                This item has no department yet — assign it under Menu → Departments so its recipe is locked to that department's stock.
              </p>
            )}
          </div>
          <Button onClick={save} disabled={!dirty || saving} className="h-12 rounded-xl bg-[hsl(var(--accent))] px-5 text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" data-testid="recipe-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save recipe
          </Button>
        </div>

        {loading ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
        ) : (
          <div className="space-y-2" data-testid="recipe-lines">
            {lines.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-6 text-center" data-testid="recipe-empty">
                <UtensilsCrossed className="mx-auto h-7 w-7 text-muted-foreground" />
                <p className="mt-2 font-semibold">No ingredients yet</p>
                <p className="text-sm text-muted-foreground">Tap <b>Add ingredient</b>, search, enter the quantity used per portion.</p>
              </div>
            ) : (
              <div className="hidden grid-cols-[1fr_110px_90px_110px_44px] gap-2 px-1 text-[10px] uppercase tracking-wide text-muted-foreground sm:grid">
                <span>Ingredient</span><span>Quantity</span><span>Unit</span><span className="text-right">Cost</span><span />
              </div>
            )}
            {lines.map((line, idx) => {
              const ing = ingById.get(line.ingredient_id);
              const opts = unitOptionsFor(ing);
              const unit = opts.includes(line.unit) ? line.unit : (ing?.unit || opts[0]);
              const cl = costLine(line.ingredient_id);
              const eff = ing?.fallback_policy;
              const foreign = Boolean(ing && selectedDept && !ingBelongsTo(ing, selectedDept));
              return (
                <div key={line._k || `line-${idx}`} className={`grid grid-cols-2 items-center gap-2 rounded-2xl border bg-background p-2 sm:grid-cols-[1fr_110px_90px_110px_44px] sm:border-0 sm:bg-transparent sm:p-0 ${foreign ? "border-destructive/50" : "border-border"}`} data-testid={`recipe-line-${idx}`}>
                  <div className="col-span-2 sm:col-span-1">
                    <IngredientPicker
                      ingredients={foreign && ing ? [ing, ...pickerIngredients] : pickerIngredients}
                      value={line.ingredient_id}
                      onChange={(id) => { const n = ingById.get(id); setLine(idx, { ingredient_id: id, unit: n?.unit || "pcs" }); }}
                      testId={`recipe-line-ing-${idx}`}
                      placeholder={selectedDept ? `Choose a ${selectedDept.name} ingredient…` : "Choose an ingredient…"}
                    />
                    {foreign ? (
                      <p className="mt-0.5 px-1 text-[11px] font-medium text-destructive" data-testid={`recipe-line-foreign-${idx}`}>
                        {ing.department_name || "Other department"} stock — not allowed in a {selectedDept.name} recipe. Pick the {selectedDept.name} equivalent.
                      </p>
                    ) : null}
                    {ing ? (
                      <p className="mt-0.5 px-1 text-[11px] text-muted-foreground" data-testid={`recipe-line-stock-${idx}`}>
                        {ing.department_name ? <>{ing.department_name} · </> : null}{fmt(ing.stock_on_hand)} {ing.unit} on the floor · {fmt(ing.back_of_house_stock)} {ing.unit} BOH
                        {eff ? <> · {eff === "auto" ? "uses fallback" : eff === "none" ? "no fallback" : "manager approval"}</> : null}
                        {ing.is_prep_item ? " · prepared in-house" : null}
                      </p>
                    ) : null}
                  </div>
                  <Input type="number" min="0" step="0.01" inputMode="decimal" value={line.quantity} onChange={(e) => setLine(idx, { quantity: e.target.value })} disabled={!line.ingredient_id} className="h-12 rounded-xl text-base tabular-nums" placeholder="qty" data-testid={`recipe-line-qty-${idx}`} />
                  <Select value={unit} onValueChange={(v) => setLine(idx, { unit: v })} disabled={!line.ingredient_id}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid={`recipe-line-unit-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{opts.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-right text-sm tabular-nums text-muted-foreground" data-testid={`recipe-line-cost-${idx}`}>
                    {cl && cl.line_cost != null ? money(cl.line_cost) : (ing && ing.cost_per_unit != null ? <span className="text-[11px]">save to cost</span> : <span className="text-[11px]">no cost set</span>)}
                  </p>
                  <Button size="icon" variant="ghost" onClick={() => removeLine(idx)} className="h-11 w-11 justify-self-end rounded-xl text-destructive hover:bg-destructive/10" aria-label="Remove ingredient" data-testid={`recipe-line-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              );
            })}
            <Button variant="outline" onClick={addLine} className="h-12 w-full rounded-xl border-dashed sm:w-auto" data-testid="recipe-add-line"><Plus className="h-4 w-4" /> Add {selectedDept ? `${selectedDept.name} ` : ""}ingredient</Button>
            {selectedDept && pickerIngredients.length === 0 ? (
              <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="recipe-no-dept-ingredients">No {selectedDept.name} ingredients yet — add them under Inventory → Ingredients ({selectedDept.name} tab) first.</p>
            ) : null}
          </div>
        )}
        {cost?.missing?.length ? (
          <p className="text-xs text-muted-foreground" data-testid="recipe-cost-missing">Cost is incomplete: {cost.missing.map((m) => m.ingredient || m.name || m).join(", ")} {cost.missing.length === 1 ? "has" : "have"} no cost per unit yet — set it under Ingredients.</p>
        ) : null}
      </CardContent>
    </Card>
  ) : null;

  return (
    <div className="space-y-4" data-testid="recipes-panel">
      {!selected ? (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
              onKeyDown={onKey}
              placeholder="Search a menu item… e.g. Cheese Burger"
              className="h-14 rounded-2xl pl-12 pr-14 text-lg"
              autoFocus
              data-testid="recipe-item-search"
            />
            {query ? <button type="button" onClick={() => setQuery("")} className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl text-muted-foreground hover:bg-secondary" aria-label="Clear" data-testid="recipe-search-clear"><X className="h-4 w-4" /></button> : null}
            {query.trim() ? (
              <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-2xl border border-border bg-card shadow-xl" role="listbox" data-testid="recipe-search-results">
                {results.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground" data-testid="recipe-search-empty">No menu item matches “{query}”.</p>
                ) : results.map((mi, i) => (
                  <button
                    key={mi.id}
                    type="button"
                    role="option"
                    aria-selected={i === highlight}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => open(mi)}
                    className={`flex min-h-14 w-full items-center justify-between gap-3 px-4 text-left ${i === highlight ? "bg-secondary" : "hover:bg-secondary/60"}`}
                    data-testid={`recipe-result-${mi.id}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-base font-semibold">{mi.name}</span>
                      <span className="block text-xs text-muted-foreground">{money(mi.price)}{mi.department ? ` · ${mi.department}` : ""}</span>
                    </span>
                    {mi.hasRecipe ? <Badge variant="outline" className="rounded-full">Has recipe</Badge> : <Badge variant="outline" className="rounded-full border-dashed text-muted-foreground">No recipe yet</Badge>}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">Type to search — results appear as you type. Select an item to see and edit its recipe. Sales deduct the listed ingredients from that department's floor stock automatically.</p>
          {deptChips.length > 1 ? (
            <div className="inline-flex flex-wrap rounded-xl border border-border bg-card p-1" data-testid="recipe-dept-filter">
              {[{ key: "all", label: "All departments" }, ...deptChips].map((c) => (
                <button key={c.key} type="button" onClick={() => setDeptFilter(c.key)} className={`h-8 rounded-lg px-3 text-sm font-medium transition-colors ${deptFilter === c.key ? "bg-secondary text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`} data-testid={`recipe-dept-filter-${c.key.replace(/\s+/g, "-")}`}>{c.label}</button>
              ))}
            </div>
          ) : null}

          {summary === null ? (
            <div className="grid gap-2 sm:grid-cols-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>
          ) : summary.length ? (
            <Card className="rounded-[24px] border-border bg-card">
              <CardContent className="p-4 sm:p-5">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium"><ChefHat className="h-4 w-4" /> Items with recipes <Badge variant="outline" className="rounded-full">{summary.filter((row) => deptFilter === "all" || itemDeptKey((menuItems || []).find((m) => m.id === row.id)) === deptFilter).length}</Badge></div>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {summary.filter((row) => deptFilter === "all" || itemDeptKey((menuItems || []).find((m) => m.id === row.id)) === deptFilter).map((row) => {
                    const mi = (menuItems || []).find((m) => m.id === row.id) || { id: row.id, name: row.name };
                    const c = costing?.[row.id];
                    return (
                      <button type="button" key={row.id} onClick={() => open(mi)} className="min-h-14 rounded-2xl border border-border bg-background px-3 py-2 text-left transition-colors hover:border-[hsl(var(--accent))] hover:bg-secondary" data-testid={`recipe-summary-${row.id}`}>
                        <span className="block truncate text-sm font-semibold">{row.name}</span>
                        <span className="block truncate text-[11px] text-muted-foreground">{mi.department ? `${mi.department} · ` : ""}{row.lines.length} ingredient{row.lines.length === 1 ? "" : "s"}{c && c.cost != null ? ` · cost ${money(c.cost)}${c.margin_pct != null ? ` · ${c.margin_pct}% margin` : ""}` : ""}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center" data-testid="recipes-empty">
              <UtensilsCrossed className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 font-semibold">No recipes yet</p>
              <p className="mt-1 text-sm text-muted-foreground">Search a menu item above to build its first recipe.</p>
            </div>
          )}
        </>
      ) : editor}
    </div>
  );
};
